import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TabsProvider } from "@/contexts/TabsContext";
import { OfflineSyncProvider } from "@/contexts/OfflineSyncContext";
import { OfflineIndicator } from "@/components/OfflineIndicator";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-accent border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

function AppContent() {
  const { user } = useAuth();

  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <OfflineSyncProvider>
                <TabsProvider>
                  <Layout />
                </TabsProvider>
              </OfflineSyncProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster position="top-right" richColors />
      <OfflineIndicator />
    </div>
  );
}

function App() {
  // Surface PWA updates via a non-blocking toast with a manual "Update" button.
  // The user keeps full control over when to reload — no surprise refreshes
  // mid-task. See utils/swRegister.js for how the event is dispatched.
  useEffect(() => {
    const onUpdate = (e) => {
      const update = e?.detail?.update;
      toast.info('A new version of SteelFlow is available.', {
        description: 'Reload to get the latest features and fixes.',
        action: update ? { label: 'Update', onClick: update } : undefined,
        duration: Infinity,
        id: 'sw-update', // prevents stacking on repeated dispatches
      });
    };
    window.addEventListener('sw:update-available', onUpdate);
    return () => window.removeEventListener('sw:update-available', onUpdate);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
